import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChatCircleDots, X, PaperPlaneTilt, SpinnerGap,
  Phone, EnvelopeSimple, Robot
} from '@phosphor-icons/react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChatWidget = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hi there! 👋 I'm your Noble Healthcare assistant. How can I help you learn about our hospice consulting services today?"
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    
    // Add user message
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        messages: messages.map(m => ({ role: m.role, content: m.content })),
        userMessage: userMessage
      });

      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: response.data.reply 
      }]);
    } catch (err) {
      console.error('Chat error:', err);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I apologize for the inconvenience. Please call us at +1 (718) 304-6214 or email noblehospicesolutions@gmail.com for immediate assistance!"
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const quickQuestions = [
    "What services do you offer?",
    "How can you help with compliance?",
    "Tell me about your training programs",
    "How do I schedule a consultation?"
  ];

  const handleQuickQuestion = (question) => {
    setInputValue(question);
  };

  // Hide ChatWidget on Client Portal (it has its own AI chat)
  if (location.pathname === '/client-portal') {
    return null;
  }

  return (
    <>
      {/* Floating Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsOpen(true)}
            className="fixed bottom-6 right-6 w-16 h-16 bg-primary text-white rounded-full shadow-2xl flex items-center justify-center z-[9999] hover:shadow-primary/30"
            data-testid="chat-widget-button"
          >
            <ChatCircleDots weight="fill" className="w-8 h-8" />
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-accent rounded-full animate-pulse" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed bottom-6 right-6 w-[380px] h-[550px] bg-white rounded-2xl shadow-2xl flex flex-col z-[9999] overflow-hidden border border-border"
            data-testid="chat-widget-window"
          >
            {/* Header */}
            <div className="bg-noble-gradient px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <Robot weight="fill" className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Noble Assistant</h3>
                  <p className="text-white/70 text-xs">Typically replies instantly</p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
                data-testid="chat-close-button"
              >
                <X weight="bold" className="w-4 h-4 text-white" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
              {messages.map((message, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] px-4 py-3 rounded-2xl ${
                      message.role === 'user'
                        ? 'bg-primary text-white rounded-br-sm'
                        : 'bg-white text-foreground border border-border rounded-bl-sm shadow-sm'
                    }`}
                  >
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>
                  </div>
                </motion.div>
              ))}
              
              {isLoading && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex justify-start"
                >
                  <div className="bg-white text-foreground border border-border px-4 py-3 rounded-2xl rounded-bl-sm shadow-sm">
                    <div className="flex items-center gap-2">
                      <SpinnerGap className="w-4 h-4 animate-spin text-primary" />
                      <span className="text-sm text-muted-foreground">Typing...</span>
                    </div>
                  </div>
                </motion.div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Quick Questions (only show if few messages) */}
            {messages.length <= 2 && (
              <div className="px-4 py-2 bg-gray-50 border-t border-border">
                <p className="text-xs text-muted-foreground mb-2">Quick questions:</p>
                <div className="flex flex-wrap gap-2">
                  {quickQuestions.map((q, i) => (
                    <button
                      key={i}
                      onClick={() => handleQuickQuestion(q)}
                      className="text-xs px-3 py-1.5 bg-white border border-border rounded-full hover:border-primary hover:text-primary transition-colors"
                      data-testid={`quick-question-${i}`}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input */}
            <form onSubmit={handleSendMessage} className="p-4 border-t border-border bg-white">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Ask about our services..."
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  disabled={isLoading}
                  className="flex-1 h-11 px-4 bg-gray-50 border border-border rounded-full text-sm focus:outline-none focus:border-primary transition-colors"
                  data-testid="chat-input"
                />
                <button
                  type="submit"
                  disabled={isLoading || !inputValue.trim()}
                  className="w-11 h-11 bg-primary text-white rounded-full flex items-center justify-center hover:bg-primary/90 transition-colors disabled:opacity-50"
                  data-testid="chat-send-button"
                >
                  <PaperPlaneTilt weight="fill" className="w-5 h-5" />
                </button>
              </div>
            </form>

            {/* Footer */}
            <div className="px-4 py-3 bg-muted/50 border-t border-border">
              <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
                <a href="tel:+17183046214" className="flex items-center gap-1 hover:text-primary transition-colors">
                  <Phone weight="fill" className="w-3 h-3" />
                  Call Us
                </a>
                <span className="text-border">|</span>
                <a href="mailto:noblehospicesolutions@gmail.com" className="flex items-center gap-1 hover:text-primary transition-colors">
                  <EnvelopeSimple weight="fill" className="w-3 h-3" />
                  Email Us
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;
